import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import HeightIcon from "@material-ui/icons/Height";
import { Grid, Box, Card, CardContent, Tooltip, Link } from "@material-ui/core";
import MapIcon from "@material-ui/icons/Map";
import PublicIcon from "@material-ui/icons/Public";

import { DisplayIconField } from "../util/DisplayField";
import { roofIcon } from "../util/svg/roofIcon";
import { altitudeIcon } from "../util/svg/altitudeIcon";
import httpClient from "../../lib/httpClient";
import { RiskAssessmentsPdf } from "./pdf/RiskAssessmentPdf";
import { LocationDescriptionPdf } from "./pdf/LocationDescriptionPdf";
//import { useContextForTableData } from "../util/pdf/useContextForTableData";
import { DownloadButton } from "../util/pdf/DownloadButton";

// DLD Button
import getHazardsList from "../riskAssessment/hazardsConfigList/allHazardsList";
import { pdfTableHazardIds } from "../util/pdf/pdfTableHazardIds";
import useGetHazardsValues from "../riskAssessment/customHooks/useGetHazardsValues";
import { emptyGeojson } from "../riskAssessment/types";

// This component given a latitude and longitude will fetch the roof details from the backend.
// It displays the roof area, building height, ground level, place name.
// It also displays the place name and a link to google maps.
// It also sets the buildingGeoJsonValue in the parent component, to display the building polygon on the map.

export const RiskRoofCard = (props) => {
  const { t } = useTranslation();
  const { placeName, latitude, longitude, setBuildingGeoJsonValueSetter } = props;

  const [roofarea, setRoofarea] = useState("...");
  const [buildingHeight, setBuildingHeight] = useState("...");
  const [groundLevel, setGroundLevel] = useState("...");

  const [pdfFileName, setPdfFileName] = useState("");
  //const tableRisksData = useContextForTableData();

  const lonlat = latitude && longitude ? ` (${latitude}, ${longitude})` : "";

  const roofTooltip =
    roofarea !== "N/A" ? t("policy.roofAreaTooltip") + lonlat : t("policy.roofAreaNoneTooltip") + lonlat;

  const googleMapsBaseUrl = "https://www.google.com/maps/search/?api=1&query=";

  const googleMapsUrl = placeName ? googleMapsBaseUrl + placeName.replace(/ /g, "+") : "";

  const allHazardsConfigs = getHazardsList();

  const pdfHazardsConfigs = allHazardsConfigs.filter((hazardConfig) =>
    pdfTableHazardIds.find((hazardCysId) => hazardConfig.cysId === hazardCysId),
  );

  const { hazardValues: tableRisksData } = useGetHazardsValues({
    hazardsList: pdfHazardsConfigs,
    lat: latitude,
    lng: longitude,
  });

  const getRoofDetailsData = useCallback(async () => {
    if (!latitude && !longitude) return;

    setBuildingHeight(".....");
    setRoofarea(".....");
    setGroundLevel(".....");
    setBuildingGeoJsonValueSetter(emptyGeojson);

    const resultFromRoofGeometry = await httpClient().get(
      `/proxy/roofGeometry?lat=${latitude}&lon=${longitude}`,
    );

    if (resultFromRoofGeometry?.data?.roof_geojsons?.length > 0) {
      setBuildingGeoJsonValueSetter(resultFromRoofGeometry.data.roof_geojsons[0]);
    } else {
      setBuildingGeoJsonValueSetter(emptyGeojson);
    }

    if (resultFromRoofGeometry?.data?.area) {
      setRoofarea(resultFromRoofGeometry.data.area);
    } else {
      setRoofarea("N/A");
    }

    if (resultFromRoofGeometry?.data?.building_height) {
      setBuildingHeight(resultFromRoofGeometry.data.building_height);
    } else {
      setBuildingHeight("N/A");
    }

    if (resultFromRoofGeometry?.data?.ground_level || resultFromRoofGeometry?.data?.ground_level === 0) {
      const asInt = parseInt(resultFromRoofGeometry.data.ground_level);
      setGroundLevel(asInt + "..." + (asInt + 10));
    } else {
      setGroundLevel("N/A");
    }
  }, [latitude, longitude, setBuildingGeoJsonValueSetter]);

  useEffect(() => {
    getRoofDetailsData();
  }, [getRoofDetailsData]);

  useEffect(() => {
    if (placeName && placeName.includes("Lat:") && placeName.includes("Lng:")) {
      setPdfFileName(`${latitude}_${longitude}`);
    } else {
      const fileNameByPlace = placeName.replace(", Italy", "").replace(/[ ,]/g, "_");

      setPdfFileName(fileNameByPlace);
    }
  }, [placeName, latitude, longitude]);

  return (
    <Grid item xs={12} sm={12}>
      <Card>
        <CardContent>
          <Box
            sx={{
              overflowX: "auto", // Enable horizontal scrolling
            }}
          >
            <Box mb={2}> {placeName}</Box>
            <Box display="flex">
              <DisplayIconField
                icon={<PublicIcon fontSize="small" />}
                tooltipMsg={t("policy.geoLocation") + placeName}
                value={parseFloat(latitude).toFixed(5) + "," + parseFloat(longitude).toFixed(5)}
              />

              <DisplayIconField icon={roofIcon} tooltipMsg={roofTooltip} value={roofarea + " m²"} />
              <DisplayIconField
                icon={<HeightIcon />}
                tooltipMsg={"Building height at centroid of building polygon"}
                value={buildingHeight + " m"}
              />

              <DisplayIconField
                icon={altitudeIcon}
                tooltipMsg={"Building ground altitude over sea level near centroid of building polygon"}
                value={groundLevel + " m"}
              />

              <Box display="flex" mr={3}>
                <Tooltip title={t("policy.checkAddressOnGoogleMaps") + placeName} placement="bottom-start">
                  <MapIcon fontSize="small" />
                </Tooltip>

                <Link href={googleMapsUrl} target="_blank" rel="noopener">
                  Google map
                </Link>

                <Box ml={2}>
                  <DownloadButton
                    documentComponent={
                      <RiskAssessmentsPdf
                        tableRisksData={tableRisksData}
                        latitude={latitude}
                        longitude={longitude}
                        renderDescription={() => (
                          <LocationDescriptionPdf
                            latitude={latitude}
                            longitude={longitude}
                            placeName={placeName}
                            buildingHeight={buildingHeight}
                            roofarea={roofarea}
                            groundLevel={groundLevel}
                          />
                        )}
                      />
                    }
                    fileName={`Valutazione_climatica__${pdfFileName}.pdf`}
                  ></DownloadButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};
